/**
 * PIN 입력 모달 컴포넌트
 * 보안 메모 접근시 PIN 입력
 */

import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button } from '../UI';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { usePin } from '../../contexts/PinContext';
import './SecureNotes.css';

function PinModal({
  isOpen,
  onClose,
  onSubmit,
  onSuccess, // Alias for onSubmit for backward compatibility
  title = 'PIN 입력',
  mode = 'verify', // verify, set, change
  showStrengthIndicator = false,
  message = ''
}) {
  // Use onSuccess if provided, otherwise fallback to onSubmit
  const handlePinSubmit = onSuccess || onSubmit;
  const { pinSettings } = usePin();
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [newPin, setNewPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const pinRefs = useRef([]);
  const newPinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  useEffect(() => {
    if (isOpen && pinRefs.current[0]) {
      pinRefs.current[0].focus();
    }
  }, [isOpen]);

  // 모달이 닫힐 때 PIN 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setPin(['', '', '', '', '', '']);
      setNewPin(['', '', '', '', '', '']);
      setConfirmPin(['', '', '', '', '', '']);
      setError('');
      setShowPin(false);
    }
  }, [isOpen]);

  // PIN 입력 처리
  const handlePinChange = (index, value, pinArray, setPinArray, refs) => {
    if (value.length > 1) return; // 1자리만 입력
    
    const newPin = [...pinArray];
    newPin[index] = value;
    setPinArray(newPin);
    
    // 다음 칸으로 자동 이동
    if (value && index < 5 && refs[index + 1]) {
      refs[index + 1].focus();
    }
  };

  // 백스페이스 처리
  const handleKeyDown = (e, index, pinArray, setPinArray, refs) => {
    if (e.key === 'Backspace' && !pinArray[index] && index > 0) {
      refs[index - 1].focus();
    }
  };

  // 붙여넣기 처리
  const handlePaste = (e, setPinArray, refs) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').slice(0, 6);
    const newPin = paste.split('').concat(Array(6).fill('')).slice(0, 6);
    setPinArray(newPin);
    
    // 마지막 입력된 칸으로 포커스
    const lastIndex = Math.min(paste.length - 1, 5);
    refs[lastIndex]?.focus();
  };

  // PIN 제출
  const handleSubmit = async () => {
    setError('');

    const pinString = pin.join('');
    const newPinString = newPin.join('');
    const confirmPinString = confirmPin.join('');


    // 유효성 검사 - 모든 PIN은 6자리로 통일
    if (mode === 'verify') {
      if (pinString.length !== 6) {
        setError('6자리 PIN을 입력해주세요.');
        return;
      }
    }
    
    if (mode === 'set' || mode === 'change') {
      if (mode === 'change' && pinString.length !== 6) {
        setError('현재 PIN을 입력해주세요.');
        return;
      }
      
      if (newPinString.length !== 6) {
        setError('새 PIN을 6자리로 입력해주세요.');
        return;
      }
      
      if (newPinString !== confirmPinString) {
        setError('새 PIN이 일치하지 않습니다.');
        return;
      }
      
      // 간단한 PIN 체크
      if (newPinString === '123456' || newPinString === '000000' || 
          newPinString === '111111' || newPinString === '999999') {
        setError('너무 간단한 PIN입니다. 다른 PIN을 사용해주세요.');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      if (!handlePinSubmit) {
        console.error('No submit handler provided to PinModal');
        setError('설정 오류가 발생했습니다.');
        setLoading(false);
        return;
      }

      let success = false;

      if (mode === 'verify') {
        success = await handlePinSubmit(pinString);
      } else if (mode === 'set') {
        success = await handlePinSubmit(newPinString);
      } else if (mode === 'change') {
        success = await handlePinSubmit(pinString, newPinString);
      }

      // 성공시에만 초기화 및 닫기
      if (success) {
        setPin(['', '', '', '', '', '']);
        setNewPin(['', '', '', '', '', '']);
        setConfirmPin(['', '', '', '', '', '']);
        onClose();
      } else {
        // 실패시 PIN 입력 초기화
        setPin(['', '', '', '', '', '']);
        setError('잘못된 PIN입니다.');
        pinRefs[0]?.focus();
      }
    } catch (err) {
      setError(err.message || 'PIN 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // PIN 강도 표시
  const getPinStrength = (pinString) => {
    if (pinString.length < 6) return { level: 0, text: '' };
    
    // 연속된 숫자 체크
    const sequential = /012|123|234|345|456|567|678|789|890|987|876|765|654|543|432|321|210/;
    if (sequential.test(pinString)) {
      return { level: 1, text: '약함', color: '#dc3545' };
    }
    
    // 반복된 숫자 체크
    const repeated = /(\d)\1{2,}/;
    if (repeated.test(pinString)) {
      return { level: 1, text: '약함', color: '#dc3545' };
    }
    
    // 다양한 숫자 사용 체크
    const uniqueDigits = new Set(pinString).size;
    if (uniqueDigits < 3) {
      return { level: 1, text: '약함', color: '#dc3545' };
    } else if (uniqueDigits < 5) {
      return { level: 2, text: '보통', color: '#ffc107' };
    } else {
      return { level: 3, text: '강함', color: '#28a745' };
    }
  };

  const renderPinInput = (pinArray, setPinArray, refs, label) => (
    <div className="pin-input-group">
      {label && <label>{label}</label>}
      <div className="pin-inputs">
        {pinArray.map((digit, index) => (
          <input
            key={index}
            ref={el => refs[index] = el}
            type={showPin ? 'text' : 'password'}
            maxLength="1"
            value={digit}
            onChange={(e) => handlePinChange(index, e.target.value, pinArray, setPinArray, refs)}
            onKeyDown={(e) => handleKeyDown(e, index, pinArray, setPinArray, refs)}
            onPaste={(e) => handlePaste(e, setPinArray, refs)}
            className="pin-digit"
            inputMode="numeric"
            pattern="[0-9]"
            autoComplete="off"
          />
        ))}
        <button
          type="button"
          className="pin-visibility-toggle"
          onClick={() => setShowPin(!showPin)}
          title={showPin ? '숨기기' : '보기'}
        >
          {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={title}
      showCloseButton={!loading}
      className="pin-modal"
    >
      <div className="pin-modal-content">
        <div className="pin-modal-icon">
          <Lock size={48} />
        </div>
        
        {mode === 'verify' && (
          <>
            <p className="pin-modal-description">
              {message || (pinSettings.hasMasterPin
                ? 'PIN을 입력하세요 (6자리).'
                : '이 메모는 PIN으로 보호되어 있습니다.')}
              {!pinSettings.hasMasterPin && (
                <>
                  <br />
                  6자리 PIN을 입력해주세요.
                </>
              )}
            </p>
            {renderPinInput(pin, setPin, pinRefs, null)}
          </>
        )}
        
        {mode === 'set' && (
          <>
            <p className="pin-modal-description">
              메모를 보호할 6자리 PIN을 설정해주세요.
            </p>
            {renderPinInput(newPin, setNewPin, newPinRefs, '새 PIN')}
            {renderPinInput(confirmPin, setConfirmPin, confirmPinRefs, 'PIN 확인')}
            
            {showStrengthIndicator && newPin.join('').length === 6 && (
              <div className="pin-strength">
                <div className="pin-strength-bar">
                  <div 
                    className="pin-strength-fill"
                    style={{
                      width: `${(getPinStrength(newPin.join('')).level / 3) * 100}%`,
                      backgroundColor: getPinStrength(newPin.join('')).color
                    }}
                  />
                </div>
                <span style={{ color: getPinStrength(newPin.join('')).color }}>
                  {getPinStrength(newPin.join('')).text}
                </span>
              </div>
            )}
          </>
        )}
        
        {mode === 'change' && (
          <>
            <p className="pin-modal-description">
              PIN을 변경합니다.
            </p>
            {renderPinInput(pin, setPin, pinRefs, '현재 PIN')}
            {renderPinInput(newPin, setNewPin, newPinRefs, '새 PIN')}
            {renderPinInput(confirmPin, setConfirmPin, confirmPinRefs, '새 PIN 확인')}
          </>
        )}
        
        {error && (
          <div className="pin-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        
        <div className="pin-modal-actions">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
          >
            {mode === 'verify' ? '확인' : mode === 'set' ? '설정' : '변경'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default PinModal;